import { Box, Text } from '@youversion/design-system-components';
import { Ach, AmericanExpress, Discover, MasterCard, Visa } from 'components/images';
import ConfirmationDialog from 'components/ui/dialogs/ConfirmationDialog';
import { useError } from 'hooks/useError';
import { useTranslation } from 'next-i18next';
import { type JSX, useMemo, useState } from 'react';
import { deletePaymentMethod } from 'utils/api';
import { firebaseLogEvent } from 'utils/firebase';

import { PaymentMethod } from '@/graphql/paymentMethods';

import PaymentMethodBox from './PaymentMethodBox';

interface Props {
  editing: boolean;
  onClick: () => void;
  paymentMethod: PaymentMethod;
  refetchPaymentMethods: () => void;
  selected: boolean;
}

const paymentMethodImages: { [key: string]: JSX.Element } = {
  Ach: <Ach />,
  'American Express': <AmericanExpress />,
  Discover: <Discover />,
  MasterCard: <MasterCard />,
  Visa: <Visa />,
};

export default function ExistingPaymentMethod({
  paymentMethod,
  selected,
  onClick,
  editing,
  refetchPaymentMethods,
}: Props) {
  const { reportError } = useError();
  const { t } = useTranslation('common');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const paymentMethodImage = useMemo(() => {
    if (paymentMethod.paymentMethodType === 'Credit Card') {
      return paymentMethod.paymentType && paymentMethodImages[paymentMethod.paymentType];
    } else if (paymentMethod.paymentMethodType === 'ACH') {
      return paymentMethodImages.Ach;
    }
  }, [paymentMethod.paymentMethodType, paymentMethod.paymentType]);

  const onEdit = () => {
    setConfirmationDialogOpen(true);
  };
  const handleDeleteClick = async (id?: number | null) => {
    firebaseLogEvent('GivingButtonTap', {
      label: 'Delete',
      screen: 'giving_payment_methods',
      value: paymentMethod.paymentType || '',
    });
    setConfirmationDialogOpen(false);
    if (id) {
      setLoading(true);
      try {
        const response = await deletePaymentMethod(id);
        refetchPaymentMethods();
        response?.errors?.forEach(e => {
          reportError('[ExistingPaymentMethod] deletePaymentMethod', e.extensions?.responseBody?.message);
        });
      } catch (error) {
        reportError('[ExistingPaymentMethod] deletePaymentMethod', error);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <PaymentMethodBox
        editing={editing}
        loading={loading}
        onClick={onClick}
        onEdit={onEdit}
        selected={selected}
        value={paymentMethod.id ?? 0}
      >
        <Box className='flex flex-col'>
          {paymentMethodImage}
          <Text className='mbs-1 mbe-1' size='lg' variant='buttonLabel'>
            {paymentMethod.displayLabel}
          </Text>
          {paymentMethod.expirationLabel ? (
            <Text size='md' variant='caption'>
              {t('giving.labels.expires', { expirationDate: paymentMethod.expirationLabel })}
            </Text>
          ) : null}
        </Box>
      </PaymentMethodBox>
      <ConfirmationDialog
        cancelLabel={t('giving.cta.cancel', 'Cancel')}
        confirmLabel={t('giving.cta.delete', 'Delete')}
        message={t('giving.messages.confirm_delete_payment_method')}
        onCancelClick={() => {
          setConfirmationDialogOpen(false);
        }}
        onClose={() => {
          setConfirmationDialogOpen(false);
        }}
        onConfirmClick={() => handleDeleteClick(paymentMethod.id)}
        open={confirmationDialogOpen}
        testId='confirm-delete-payment-method'
      />
    </>
  );
}
